import z from "zod";

export type TableExportDelivery =
  | { delivery: "download"; downloadUrl: string }
  | { delivery: "background"; publicAccessToken: string; runId: string };

export class TableExportDownloadError extends Error {
  constructor(readonly status: number) {
    super("Export download failed.");
    this.name = "TableExportDownloadError";
  }
}

const statusSchema = z.discriminatedUnion("state", [
  z.object({ downloadUrl: z.string(), state: z.literal("complete") }),
  z.object({ message: z.string(), state: z.literal("failed") }),
  z.object({ state: z.literal("pending") }),
]);

const POLL_INTERVAL_MS = 1_500;
const POLL_TIMEOUT_MS = 60 * 60 * 1_000;

type TableExportDownloadDependencies = {
  fetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  now: () => number;
  saveBlob: (blob: Blob, fileName: string) => void;
  sleep: (durationMs: number) => Promise<void>;
};

export function saveTableExportBlob(blob: Blob, fileName: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = fileName;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  globalThis.setTimeout(() => URL.revokeObjectURL(objectUrl), 1_000);
}

const defaultDependencies: TableExportDownloadDependencies = {
  fetch: (input, init) => globalThis.fetch(input, init),
  now: Date.now,
  saveBlob: saveTableExportBlob,
  sleep: async (durationMs) => await new Promise((resolve) => globalThis.setTimeout(resolve, durationMs)),
};

function getDownloadFileName(response: Response): string {
  const disposition = response.headers.get("Content-Disposition") ?? "";
  const match = /filename="([^"]+)"/.exec(disposition);
  return match?.[1] ?? "export";
}

async function startDownload(
  downloadUrl: string,
  dependencies: TableExportDownloadDependencies,
): Promise<void> {
  const response = await dependencies.fetch(downloadUrl, {
    cache: "no-store",
    credentials: "same-origin",
  });
  if (!response.ok) {
    throw new TableExportDownloadError(response.status);
  }

  dependencies.saveBlob(await response.blob(), getDownloadFileName(response));
}

async function waitForBackgroundExport(args: {
  publicAccessToken: string;
  runId: string;
}, dependencies: TableExportDownloadDependencies): Promise<string> {
  const deadline = dependencies.now() + POLL_TIMEOUT_MS;

  while (dependencies.now() < deadline) {
    const response = await dependencies.fetch(
      `/api/exports/runs/${encodeURIComponent(args.runId)}/status`,
      {
        body: JSON.stringify({ publicAccessToken: args.publicAccessToken }),
        cache: "no-store",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        method: "POST",
      },
    );
    if (!response.ok) {
      throw new Error("Export status unavailable.");
    }

    const status = statusSchema.parse(await response.json());
    if (status.state === "complete") return status.downloadUrl;
    if (status.state === "failed") throw new Error(status.message);

    await dependencies.sleep(POLL_INTERVAL_MS);
  }

  throw new Error("Export is taking too long. Try again.");
}

export function createTableExportDownloader(
  dependencyOverrides: Partial<TableExportDownloadDependencies> = {},
) {
  const dependencies = { ...defaultDependencies, ...dependencyOverrides };

  return async function downloadTableExportResult(delivery: TableExportDelivery): Promise<void> {
    const downloadUrl = delivery.delivery === "download"
      ? delivery.downloadUrl
      : await waitForBackgroundExport(delivery, dependencies);

    await startDownload(downloadUrl, dependencies);
  };
}

export const downloadTableExport = createTableExportDownloader();
